'use client'

import { useEffect, useState } from 'react'
import { TFunction } from 'react-i18next'

import * as Sentry from '@sentry/nextjs'
import { useParams } from 'next/navigation'

import { useTranslation } from '@/app/components/providers/TranslationContext'

import { logFrontendError } from './_lib/frontendLogging'
import { ErrorPage as UIErrorPage } from './ErrorPage'

const ErrorPage = ({ error }: { error: any }) => {
  const t: TFunction = useTranslation()

  const [correlationId, setCorrelationId] = useState<string>('')
  const [basketId, setBasketId] = useState<string>('')

  const params = useParams()

  useEffect(() => {
    ;(async () => {
      if (error) {
        try {
          Sentry.captureException(error)
        } catch (sentryError) {
          // Sentry call failed - likely blocked by ad blockers or privacy extensions
          console.warn('Failed to capture exception in Sentry:', sentryError)
        }
        const isBackendError = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(error?.message ?? '')

        // error.digest is the only value Next.js reliably passes through the server→client boundary in production
        const resolvedCorrelationId = isBackendError ? error.message : (error as any)?.digest

        if (resolvedCorrelationId) {
          setCorrelationId(resolvedCorrelationId)
        }

        logFrontendError(`A ${resolvedCorrelationId ? 'server' : 'frontend'} error happened`, {
          page: window.location?.pathname,
          ...(resolvedCorrelationId && { correlationId: resolvedCorrelationId }),
          ...(!resolvedCorrelationId && { errorMessage: error?.message }),
        })
      }
    })()
  }, [error])

  useEffect(() => {
    const storedBasket = sessionStorage.getItem('basket')
    if (storedBasket) {
      try {
        const basket = JSON.parse(storedBasket)
        if (basket?.basketId) {
          setBasketId(basket.basketId)
        }
      } catch (e) {
        logFrontendError('Failed to parse basket from session storage', { errorMessage: (e as Error)?.message })
      }
    }
  }, [])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('triggerSurvey'))
    }, 2000)

    return () => window.clearTimeout(timeoutId)
  }, [])

  return (
    <UIErrorPage
      basketId={basketId}
      correlationId={correlationId}
      onBackToHomeClicked={() => (window.location.href = window?.location?.origin + `/${params?.locale}/${params?.ferry}`)}
      onReloadClicked={() => window?.location?.reload()}
      resources={{
        apologise: t('ERROR-PAGE-WE-APOLOGISE'),
        backToHomeButtonText: t('RETURN-TO-HOMEPAGE'),
        message: t('ERROR-PAGE-MESSAGE'),
        title: t('ERROR-PAGE-TITLE'),
        tryAgainButtonText: t('TRY-AGAIN'),
      }}
    />
  )
}

export default ErrorPage
